import React, { useEffect, useRef, useState } from 'react';
import {
    Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, Grid, IconButton, LinearProgress, MenuItem, TextField, Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
    AddPhotoAlternateRounded, CloseRounded, CloudDownloadRounded, DeleteRounded, RefreshRounded, StorefrontRounded,
} from '@material-ui/icons';
import { toast } from 'react-toastify';

// Génération d'images produit par IA (contrat « images IA ») : le navigateur envoie le prompt,
// les images de référence et les options d'adaptation ; storehouse relaie vers ai-services.
// Les images reçues sont des data-URL rendues uniquement par <img src> : aucun HTML n'est injecté.

const MODELES = [
    { value: 'gpt-image-2', label: 'GPT Image 2' },
    { value: 'nano-banana-2', label: 'Nano Banana 2' },
];
const TYPES_ACCEPTES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_REFERENCES = 6;
const MAX_PROMPT = 4000;
const COTE_MAX_REFERENCE = 1600;
const OCTETS_MAX_REFERENCE = 8 * 1024 * 1024;
const POIDS_MAX_MO = 8; // plafond du poids max de l'image adaptée

const ADAPT_DEFAUT = {
    enabled: true,
    square: true,
    background: '#FFFFFF',
    size: 1200,
    format: 'jpeg',
    quality: 85,
    max_mo: 2,
};

// Les erreurs du serveur sont volontairement génériques : le message lisible se déduit du code.
const MESSAGES_GENERATION = {
    400: "Requête refusée : vérifiez le prompt, le nombre d'images et les images de référence.",
    401: "Session expirée : reconnectez-vous puis réessayez.",
    403: "Vous n'avez pas le droit de générer des images pour ce produit.",
    413: "Images de référence trop lourdes : retirez-en ou utilisez des images plus petites.",
    502: "Le service de génération n'a rendu aucune image ou est en erreur. Réessayez ou changez de modèle.",
    503: "La génération d'images n'est pas configurée sur le serveur.",
    504: "La génération a pris trop de temps. Réessayez, éventuellement avec moins d'images.",
    429: "Quota de génération atteint ou trop de générations en cours. Patientez quelques instants avant de réessayer.",
};
const MESSAGES_AJOUT = {
    400: "Image refusée par le serveur (format ou poids).",
    429: "Trop de requêtes en cours. Patientez quelques instants avant de réessayer.",
    401: "Session expirée : reconnectez-vous puis réessayez.",
    403: "Vous n'avez pas le droit de modifier ce produit.",
    404: "Produit introuvable.",
};

// Pour un 400 (validation) ou un 429 (quota), le serveur relaie un message utile : il complète
// le message générique du code. Pour les autres codes, il est générique et n'apporte rien.
function messageErreur(table, status, defaut, detailServeur) {
    const base = table[status] || `${defaut} (code ${status}).`;
    if ((status === 400 || status === 429) && typeof detailServeur === 'string' && detailServeur.trim()) {
        return `${base} Détail : ${detailServeur.trim()}`;
    }
    return base;
}

// Photos de la fiche que le serveur a écartées des références (illisibles ou trop lourdes).
// Le contrat n'en fixe pas la forme exacte : nom de fichier seul, ou objet avec un motif.
function libellePhotoIgnoree(p) {
    if (typeof p === 'string') return p;
    if (p && typeof p === 'object') {
        const nom = p.filename || p.name || '';
        const motif = p.reason || p.error || '';
        return motif ? `${nom} (${motif})` : nom;
    }
    return '';
}

function dataUrl(image) {
    // Le type sert à construire l'adresse : on n'accepte que les trois formats attendus.
    const mime = TYPES_ACCEPTES.includes(image.mime) ? image.mime : 'image/png';
    return `data:${mime};base64,${image.data}`;
}

function extension(mime) {
    return mime === 'image/jpeg' ? 'jpg' : mime === 'image/webp' ? 'webp' : 'png';
}

function formatPoids(octets) {
    if (!octets && octets !== 0) return '';
    if (octets < 1024 * 1024) return `${Math.round(octets / 1024)} Ko`;
    return `${(octets / (1024 * 1024)).toFixed(2)} Mo`;
}

function octetsBase64(data) {
    const remplissage = data.endsWith('==') ? 2 : data.endsWith('=') ? 1 : 0;
    return Math.floor(data.length * 3 / 4) - remplissage;
}

// Réduit une image téléversée à COTE_MAX_REFERENCE pixels de côté : les modèles n'en tirent pas
// davantage, et le poids envoyé reste raisonnable. Le PNG garde sa transparence, le reste passe en JPEG.
function reduireImage(fichier) {
    return new Promise((resolve, reject) => {
        const lecteur = new FileReader();
        lecteur.onerror = () => reject(new Error(`Lecture impossible : ${fichier.name}`));
        lecteur.onload = () => {
            const img = new Image();
            img.onerror = () => reject(new Error(`Image illisible : ${fichier.name}`));
            img.onload = () => {
                const echelle = Math.min(1, COTE_MAX_REFERENCE / Math.max(img.width, img.height));
                const largeur = Math.max(1, Math.round(img.width * echelle));
                const hauteur = Math.max(1, Math.round(img.height * echelle));
                const canvas = document.createElement('canvas');
                canvas.width = largeur;
                canvas.height = hauteur;
                const ctx = canvas.getContext('2d');
                const mime = fichier.type === 'image/png' ? 'image/png' : 'image/jpeg';
                if (mime === 'image/jpeg') {
                    ctx.fillStyle = '#FFFFFF';
                    ctx.fillRect(0, 0, largeur, hauteur);
                }
                ctx.drawImage(img, 0, 0, largeur, hauteur);
                const url = canvas.toDataURL(mime, 0.9);
                const data = url.slice(url.indexOf(',') + 1);
                if (octetsBase64(data) > OCTETS_MAX_REFERENCE) {
                    reject(new Error(`Image trop lourde même réduite : ${fichier.name}`));
                    return;
                }
                resolve({ data, mime, nom: fichier.name, largeur, hauteur });
            };
            img.src = lecteur.result;
        };
        lecteur.readAsDataURL(fichier);
    });
}

function telecharger(image, nomBase, index) {
    const binaire = atob(image.data);
    const octets = new Uint8Array(binaire.length);
    for (let i = 0; i < binaire.length; i++) octets[i] = binaire.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([octets], { type: image.mime }));
    const lien = document.createElement('a');
    lien.href = url;
    lien.download = `${nomBase || 'image'}-ia-${index + 1}.${extension(image.mime)}`;
    document.body.appendChild(lien);
    lien.click();
    document.body.removeChild(lien);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const LIBELLES_STATUT = {
    added: { label: 'ajoutée', color: 'primary' },
    skipped: { label: 'ignorée', color: 'default' },
    error: { label: 'erreur', color: 'secondary' },
};

function ResultatAjout({ resultat }) {
    if (!resultat) return null;
    const { prestashop, prestashop_error } = resultat;
    return (
        <Box mt={1}>
            <Typography variant="caption" display="block" style={{ color: '#2e7d32' }}>
                Photo enregistrée sur la fiche.
            </Typography>
            {prestashop_error &&
                <Alert severity="warning" style={{ marginTop: 4 }}>
                    Envoi PrestaShop : {prestashop_error}
                </Alert>
            }
            {!prestashop_error && !prestashop &&
                <Typography variant="caption" color="textSecondary" display="block">
                    Aucun envoi PrestaShop (produit non activé pour l'e-commerce sur ce compte).
                </Typography>
            }
            {prestashop && Array.isArray(prestashop.results) && prestashop.results.length === 0 &&
                <Typography variant="caption" color="textSecondary" display="block">
                    Aucune boutique PrestaShop rattachée à ce compte.
                </Typography>
            }
            {prestashop && Array.isArray(prestashop.results) && prestashop.results.map((r, i) => {
                const statut = LIBELLES_STATUT[r.status] || { label: r.status, color: 'default' };
                return (
                    <Box key={`${r.store_id}-${i}`} display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 6 }} mt={0.5}>
                        <Chip size="small" icon={<StorefrontRounded />} label={r.store_id} variant="outlined" />
                        <Chip size="small" label={statut.label} color={statut.color} />
                        {r.ps_image_id ? <Typography variant="caption">image n° {r.ps_image_id}</Typography> : null}
                        {r.error ? <Typography variant="caption" color="error">{r.error}</Typography> : null}
                    </Box>
                );
            })}
        </Box>
    );
}

export default function EcomImageGenerator({ product, onPhotoAdded }) {
    const [ouvert, setOuvert] = useState(false);
    const [model, setModel] = useState(MODELES[0].value);
    const [prompt, setPrompt] = useState('');
    const [n, setN] = useState(1);
    const [televersees, setTeleversees] = useState([]); // { data, mime, nom }
    const [photosFiche, setPhotosFiche] = useState([]); // noms de fichiers cochés
    const [adapt, setAdapt] = useState(ADAPT_DEFAUT);
    const [enCours, setEnCours] = useState(false);
    const [secondes, setSecondes] = useState(0);
    const [erreur, setErreur] = useState('');
    const [images, setImages] = useState([]);
    const [photosIgnorees, setPhotosIgnorees] = useState([]);
    const [ajouts, setAjouts] = useState({}); // index -> { enCours, resultat, erreur }
    const [visionneuse, setVisionneuse] = useState(null);
    const chrono = useRef(null);
    const inputFichiers = useRef(null);

    useEffect(() => () => clearInterval(chrono.current), []);

    const photosDisponibles = (product && Array.isArray(product.photos) ? product.photos : [])
        .filter((p) => p && p.filename);
    const totalReferences = televersees.length + photosFiche.length;

    const ajouterFichiers = async (event) => {
        const fichiers = Array.from(event.target.files || []);
        event.target.value = '';
        const place = MAX_REFERENCES - totalReferences;
        const refuses = fichiers.filter((f) => !TYPES_ACCEPTES.includes(f.type));
        if (refuses.length) {
            toast.error(`Format non accepté (JPEG, PNG ou WebP) : ${refuses.map((f) => f.name).join(', ')}`);
        }
        const acceptes = fichiers.filter((f) => TYPES_ACCEPTES.includes(f.type));
        if (acceptes.length > place) {
            toast.warn(`${MAX_REFERENCES} images de référence au plus : ${acceptes.length - place} ignorée(s).`);
        }
        const retenus = acceptes.slice(0, Math.max(0, place));
        const reduites = [];
        for (const fichier of retenus) {
            try {
                reduites.push(await reduireImage(fichier));
            } catch (e) {
                toast.error(e.message);
            }
        }
        if (reduites.length) setTeleversees((prev) => [...prev, ...reduites].slice(0, MAX_REFERENCES));
    };

    const basculerPhotoFiche = (filename) => {
        if (photosFiche.includes(filename)) {
            setPhotosFiche(photosFiche.filter((f) => f !== filename));
            return;
        }
        if (totalReferences >= MAX_REFERENCES) {
            toast.warn(`${MAX_REFERENCES} images de référence au plus.`);
            return;
        }
        setPhotosFiche([...photosFiche, filename]);
    };

    const changerAdapt = (champ, valeur) => setAdapt((prev) => ({ ...prev, [champ]: valeur }));

    const generer = async () => {
        const texte = prompt.trim();
        if (!texte) {
            setErreur('Le prompt est obligatoire.');
            return;
        }
        setErreur('');
        setPhotosIgnorees([]);
        setEnCours(true);
        setSecondes(0);
        clearInterval(chrono.current);
        chrono.current = setInterval(() => setSecondes((s) => s + 1), 1000);
        try {
            const qualite = Math.min(100, Math.max(1, parseInt(adapt.quality, 10) || ADAPT_DEFAUT.quality));
            const maxMo = Math.min(POIDS_MAX_MO, Math.max(0.1, parseFloat(adapt.max_mo) || ADAPT_DEFAUT.max_mo));
            const reponse = await fetch('/seo/image/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify({
                    model,
                    prompt: texte,
                    n,
                    reference_images: televersees.map(({ data, mime }) => ({ data, mime })),
                    product_id: product.id,
                    product_photos: photosFiche,
                    adapt: {
                        enabled: !!adapt.enabled,
                        square: !!adapt.square,
                        background: adapt.background,
                        size: adapt.size,
                        format: adapt.format,
                        quality: qualite,
                        max_bytes: Math.round(maxMo * 1000 * 1000),
                    },
                }),
            });
            const resultat = await reponse.json().catch(() => ({}));
            if (!reponse.ok || !resultat.success) {
                throw new Error(messageErreur(MESSAGES_GENERATION, reponse.status, 'Erreur lors de la génération', resultat.error));
            }
            const recues = (Array.isArray(resultat.images) ? resultat.images : [])
                .filter((img) => img && typeof img.data === 'string' && img.data);
            setPhotosIgnorees((Array.isArray(resultat.skipped_photos) ? resultat.skipped_photos : [])
                .map(libellePhotoIgnoree).filter(Boolean));
            if (!recues.length) throw new Error(MESSAGES_GENERATION[502]);
            setImages(recues);
            setAjouts({});
        } catch (e) {
            setErreur(e.message || 'Erreur lors de la génération');
        } finally {
            clearInterval(chrono.current);
            setEnCours(false);
        }
    };

    const ajouterAPrestashop = async (image, index) => {
        setAjouts((prev) => ({ ...prev, [index]: { enCours: true } }));
        try {
            const reponse = await fetch(`/stocks/product/${encodeURIComponent(product.id)}/photos/ai`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify({
                    image: { data: image.data, mime: image.mime },
                    name: product.name || '',
                    push_prestashop: true,
                }),
            });
            const resultat = await reponse.json().catch(() => ({}));
            if (!reponse.ok || !resultat.photo) {
                throw new Error(messageErreur(MESSAGES_AJOUT, reponse.status, "Erreur lors de l'enregistrement", resultat.error));
            }
            setAjouts((prev) => ({ ...prev, [index]: { resultat } }));
            if (onPhotoAdded) onPhotoAdded(resultat.photo);
            toast.success(resultat.prestashop_error
                ? "Photo enregistrée, mais l'envoi PrestaShop a échoué."
                : 'Photo enregistrée sur la fiche.');
        } catch (e) {
            setAjouts((prev) => ({ ...prev, [index]: { erreur: e.message } }));
            toast.error(e.message);
        }
    };

    const fermer = () => {
        if (enCours) return;
        setOuvert(false);
    };

    return (
        <>
            <Button
                size="small"
                color="secondary"
                startIcon={<AddPhotoAlternateRounded />}
                onClick={() => setOuvert(true)}
            >
                Générer une image IA
            </Button>

            <Dialog open={ouvert} onClose={fermer} fullWidth maxWidth="lg" scroll="paper">
                <DialogTitle>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <span>Générer une image IA — {product.name}</span>
                        <IconButton onClick={fermer} disabled={enCours}><CloseRounded /></IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={5}>
                            <TextField
                                select fullWidth variant="outlined" size="small" label="Modèle"
                                value={model} onChange={(e) => setModel(e.target.value)} disabled={enCours}
                            >
                                {MODELES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                            </TextField>

                            <Box mt={2}>
                                <TextField
                                    fullWidth multiline minRows={5} variant="outlined" label="Prompt"
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value.slice(0, MAX_PROMPT))}
                                    disabled={enCours}
                                    placeholder="Ex. : le produit posé sur un fond blanc, éclairage studio doux, vue de trois quarts…"
                                    helperText={`${prompt.length}/${MAX_PROMPT}`}
                                />
                            </Box>

                            <Box mt={2}>
                                <TextField
                                    select variant="outlined" size="small" label="Nombre d'images"
                                    value={n} onChange={(e) => setN(Number(e.target.value))} disabled={enCours}
                                    style={{ minWidth: 160 }}
                                >
                                    {[1, 2, 3, 4].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                                </TextField>
                            </Box>

                            <Box mt={3}>
                                <Typography variant="subtitle2">
                                    Images de référence ({totalReferences}/{MAX_REFERENCES})
                                </Typography>
                                <input
                                    ref={inputFichiers} type="file" accept={TYPES_ACCEPTES.join(',')} multiple
                                    style={{ display: 'none' }} onChange={ajouterFichiers}
                                />
                                <Button
                                    size="small" variant="outlined" style={{ marginTop: 8 }}
                                    disabled={enCours || totalReferences >= MAX_REFERENCES}
                                    onClick={() => inputFichiers.current && inputFichiers.current.click()}
                                >
                                    Téléverser des images
                                </Button>
                                {televersees.length > 0 &&
                                    <Grid container spacing={1} style={{ marginTop: 8 }}>
                                        {televersees.map((ref, i) => (
                                            <Grid item xs={4} key={`${ref.nom}-${i}`}>
                                                <Box position="relative">
                                                    <img src={dataUrl(ref)} alt={ref.nom}
                                                        style={{ width: '100%', height: 90, objectFit: 'contain', border: '1px solid #e0e0e0', borderRadius: 4, background: '#fafafa' }} />
                                                    <IconButton
                                                        size="small" disabled={enCours}
                                                        onClick={() => setTeleversees(televersees.filter((_, j) => j !== i))}
                                                        style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(255,255,255,0.85)' }}
                                                    >
                                                        <DeleteRounded fontSize="small" color="error" />
                                                    </IconButton>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                }
                                {photosDisponibles.length > 0 &&
                                    <>
                                        <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 12 }}>
                                            Photos de la fiche (cochez pour les utiliser)
                                        </Typography>
                                        <Grid container spacing={1}>
                                            {photosDisponibles.map((photo) => {
                                                const coche = photosFiche.includes(photo.filename);
                                                return (
                                                    <Grid item xs={4} key={photo.filename}>
                                                        <Box
                                                            position="relative"
                                                            onClick={() => !enCours && basculerPhotoFiche(photo.filename)}
                                                            style={{ cursor: enCours ? 'default' : 'pointer', border: coche ? '2px solid #e91e63' : '2px solid transparent', borderRadius: 4 }}
                                                        >
                                                            <img src={`/stocks/photos/${photo.filename}`} alt=""
                                                                style={{ width: '100%', height: 90, objectFit: 'contain', background: '#fafafa', display: 'block' }} />
                                                            <Checkbox
                                                                size="small" checked={coche} disabled={enCours}
                                                                style={{ position: 'absolute', top: 0, left: 0, padding: 2, background: 'rgba(255,255,255,0.85)' }}
                                                            />
                                                        </Box>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </>
                                }
                            </Box>

                            <Box mt={3} p={2} style={{ background: '#fdf5f8', borderRadius: 8, border: '1px solid #f48fb1' }}>
                                <FormControlLabel
                                    control={<Checkbox checked={adapt.enabled} onChange={(e) => changerAdapt('enabled', e.target.checked)} disabled={enCours} />}
                                    label="Adapter pour PrestaShop"
                                />
                                {adapt.enabled &&
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <FormControlLabel
                                                control={<Checkbox checked={adapt.square} onChange={(e) => changerAdapt('square', e.target.checked)} disabled={enCours} />}
                                                label="Carré (marges, sans recadrage)"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                                <input type="color" value={adapt.background} disabled={enCours}
                                                    onChange={(e) => changerAdapt('background', e.target.value.toUpperCase())}
                                                    style={{ width: 40, height: 32, border: 'none', background: 'none', padding: 0 }} />
                                                <Typography variant="body2">Fond {adapt.background}</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField select fullWidth variant="outlined" size="small" label="Taille (px)"
                                                value={adapt.size} onChange={(e) => changerAdapt('size', Number(e.target.value))} disabled={enCours}>
                                                {[800, 1200, 2000].map((v) => <MenuItem key={v} value={v}>{v} × {v}</MenuItem>)}
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField select fullWidth variant="outlined" size="small" label="Format"
                                                value={adapt.format} onChange={(e) => changerAdapt('format', e.target.value)} disabled={enCours}>
                                                <MenuItem value="jpeg">JPEG</MenuItem>
                                                <MenuItem value="webp">WebP</MenuItem>
                                                <MenuItem value="png">PNG</MenuItem>
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField fullWidth variant="outlined" size="small" type="number" label="Qualité (1-100)"
                                                value={adapt.quality} onChange={(e) => changerAdapt('quality', e.target.value)}
                                                disabled={enCours || adapt.format === 'png'}
                                                inputProps={{ min: 1, max: 100 }}
                                                helperText={adapt.format === 'png' ? 'Sans effet en PNG' : ''} />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField fullWidth variant="outlined" size="small" type="number" label="Poids max (Mo)"
                                                value={adapt.max_mo}
                                                onChange={(e) => {
                                                    const v = parseFloat(e.target.value);
                                                    changerAdapt('max_mo', v > POIDS_MAX_MO ? POIDS_MAX_MO : e.target.value);
                                                }}
                                                disabled={enCours} inputProps={{ min: 0.1, max: POIDS_MAX_MO, step: 0.1 }}
                                                helperText={`${POIDS_MAX_MO} Mo au plus`} />
                                        </Grid>
                                    </Grid>
                                }
                            </Box>

                            <Box mt={3}>
                                <Button
                                    fullWidth variant="contained" color="secondary"
                                    disabled={enCours || !prompt.trim()}
                                    onClick={generer}
                                    startIcon={enCours ? <CircularProgress size={18} color="inherit" /> : (images.length ? <RefreshRounded /> : <AddPhotoAlternateRounded />)}
                                >
                                    {enCours ? 'Génération en cours…' : images.length ? 'Régénérer' : 'Générer'}
                                </Button>
                                {enCours &&
                                    <Box mt={1}>
                                        <LinearProgress color="secondary" />
                                        <Typography variant="caption" color="textSecondary">
                                            {secondes} s écoulées — la génération peut dépasser une minute.
                                        </Typography>
                                    </Box>
                                }
                                {erreur && <Alert severity="error" style={{ marginTop: 8 }}>{erreur}</Alert>}
                                {photosIgnorees.length > 0 &&
                                    <Alert severity="warning" style={{ marginTop: 8 }}>
                                        Photos de la fiche ignorées (illisibles ou trop lourdes) : {photosIgnorees.join(', ')}
                                    </Alert>
                                }
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={7}>
                            {!images.length &&
                                <Box display="flex" alignItems="center" justifyContent="center" style={{ minHeight: 300, border: '1px dashed #ccc', borderRadius: 8 }}>
                                    <Typography color="textSecondary">Les images générées apparaîtront ici.</Typography>
                                </Box>
                            }
                            <Grid container spacing={2}>
                                {images.map((image, i) => {
                                    const ajout = ajouts[i] || {};
                                    return (
                                        <Grid item xs={12} sm={6} key={i}>
                                            <Box p={1} style={{ border: '1px solid #e0e0e0', borderRadius: 8 }}>
                                                <img
                                                    src={dataUrl(image)} alt={`Rendu IA ${i + 1}`}
                                                    onClick={() => setVisionneuse(image)}
                                                    style={{ width: '100%', maxHeight: 320, objectFit: 'contain', cursor: 'zoom-in', background: '#fafafa', display: 'block' }}
                                                />
                                                <Typography variant="caption" color="textSecondary" display="block">
                                                    {image.width && image.height ? `${image.width} × ${image.height} px · ` : ''}
                                                    {formatPoids(image.bytes || octetsBase64(image.data))} · {extension(image.mime).toUpperCase()}
                                                </Typography>
                                                <Box display="flex" flexWrap="wrap" style={{ gap: 8 }} mt={1}>
                                                    <Button
                                                        size="small" variant="contained" color="primary"
                                                        disabled={ajout.enCours || !!ajout.resultat || enCours}
                                                        startIcon={ajout.enCours ? <CircularProgress size={14} color="inherit" /> : <StorefrontRounded />}
                                                        onClick={() => ajouterAPrestashop(image, i)}
                                                    >
                                                        {ajout.resultat ? 'Ajoutée' : 'Ajouter aux images PrestaShop'}
                                                    </Button>
                                                    <Button size="small" startIcon={<CloudDownloadRounded />}
                                                        onClick={() => telecharger(image, product.ref || product.id, i)}>
                                                        Télécharger
                                                    </Button>
                                                </Box>
                                                {ajout.erreur && <Alert severity="error" style={{ marginTop: 6 }}>{ajout.erreur}</Alert>}
                                                <ResultatAjout resultat={ajout.resultat} />
                                            </Box>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={fermer} disabled={enCours}>Fermer</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!visionneuse} onClose={() => setVisionneuse(null)} fullScreen>
                {visionneuse &&
                    <Box display="flex" flexDirection="column" height="100%" style={{ background: '#111' }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1} style={{ color: '#fff' }}>
                            <Typography variant="body2">
                                {visionneuse.width && visionneuse.height ? `${visionneuse.width} × ${visionneuse.height} px · ` : ''}
                                {formatPoids(visionneuse.bytes || octetsBase64(visionneuse.data))} · {extension(visionneuse.mime).toUpperCase()}
                            </Typography>
                            <IconButton onClick={() => setVisionneuse(null)} style={{ color: '#fff' }}><CloseRounded /></IconButton>
                        </Box>
                        <Box flex={1} display="flex" alignItems="center" justifyContent="center" p={2} style={{ overflow: 'auto' }}>
                            <img src={dataUrl(visionneuse)} alt="Rendu IA agrandi" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        </Box>
                    </Box>
                }
            </Dialog>
        </>
    );
}
